import { assertInInjectionContext, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, map, Observable, startWith } from 'rxjs';

import { cosProvide } from '@cosmos/core';
import { LocalStorageService } from '@cosmos/data-access-storage';
import { collectRouteQueryParams, getActiveRoute } from '@cosmos/router';
import {
  createRetainAccessParams,
  hasRetainAccessQueryParam,
  hasRetainAccessRouteData,
  RETAIN_ACCESS_CODE_LINK_CONFIG,
  type RetainAccessCodeLinkConfig,
} from '@customer-portal/common/util-retain-access-code';

import { CustomerPortalCollectionAuthQueries } from '../queries';

const getActivatedRouteThatRequiresAccessCodeRetention = ({
  route,
  router,
  startWithCurrentRoute = false,
}: {
  route: ActivatedRoute;
  router: Router;
  startWithCurrentRoute: boolean;
}): Observable<ActivatedRouteSnapshot> => {
  // listen for navigation changes and if needed reapply accessCode query param
  return router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    // start with the current route if needed
    startWithCurrentRoute
      ? startWith(getActiveRoute(route.snapshot))
      : startWith(),
    // map to active route
    map(() => getActiveRoute(route.snapshot)),
    // filter only routes that marked as routes that require access code retention
    filter((activeRoute) => hasRetainAccessRouteData(activeRoute.data))
  );
};

const injectRetainAccessCodeStorage = () => {
  // session storage doesn't work for new tab so we can no use it,
  // instead we use local storage, the only thing we must be sure if the key is unique enough
  // to not cause conflicts with other potential sessions
  return inject(LocalStorageService);
};

const RETAINED_ACCESS_CODE_STORAGE_KEY = 'retainedCollectionAccessCode';

function hasRetainCollectionAccessFlag({
  storage,
  queryParams,
  accessCode,
}: {
  storage: Storage;
  queryParams: Record<string, any>;
  accessCode: string | null | undefined;
}) {
  // we have to check both query param and storage because if user opened collection link in the new tab,
  // we will not have query param but we can have access code in storage from previous session
  return (
    hasRetainAccessQueryParam(queryParams) ||
    (accessCode &&
      accessCode === storage.getItem(RETAINED_ACCESS_CODE_STORAGE_KEY))
  );
}

function createTimingBucket({ timeWindowMs = 500 }: { timeWindowMs?: number }) {
  let recentRunTimestamps: number[] = [];
  const revalidate = (base = Date.now()) => {
    recentRunTimestamps = recentRunTimestamps.filter(
      (t) => base - t < timeWindowMs
    );
  };
  return {
    increment: () => {
      const now = Date.now();
      revalidate(now);
      recentRunTimestamps.push(now);
    },
    getLength: () => {
      revalidate();
      return recentRunTimestamps.length;
    },
    clear: () => {
      recentRunTimestamps = [];
    },
  };
}

/**
 * Retains collection access code in the URL query parameters
 * for routes that have the appropriate route data set.
 *
 * Mirrors the presentation access code retention so collections behave the
 * same way: the access code stays in the URL across in-collection navigation.
 */
export function retainCollectionAccessCodeIfNeeded() {
  assertInInjectionContext(retainCollectionAccessCodeIfNeeded);

  const store = inject(Store);
  const router = inject(Router);
  const route = inject(ActivatedRoute);
  const queryParams = collectRouteQueryParams(route.snapshot);
  const accessCode = store.selectSignal(
    CustomerPortalCollectionAuthQueries.getAccessCode
  );
  const storage = injectRetainAccessCodeStorage();

  if (
    !hasRetainCollectionAccessFlag({
      storage,
      queryParams,
      accessCode: accessCode(),
    })
  ) {
    // if we don't have query param and no access code in storage, we can not guarantee that user was logged in via collection so we just skip
    return;
  }

  if (
    hasRetainAccessQueryParam(queryParams) &&
    storage.getItem(RETAINED_ACCESS_CODE_STORAGE_KEY) !== accessCode()
  ) {
    // if it's "collection shared link" session and key is not the same as access code -
    // it's the first time we are processing this shared collection,
    // so we need to store access code in storage for potential future use in different browser tabs
    storage.setItem(RETAINED_ACCESS_CODE_STORAGE_KEY, accessCode()!);
  }

  const bucket = createTimingBucket({ timeWindowMs: 500 });
  // listen for navigation changes and if needed reapply accessCode query param
  getActivatedRouteThatRequiresAccessCodeRetention({
    route,
    router,
    startWithCurrentRoute: false,
  })
    .pipe(
      filter((activeRoute) => {
        return (
          !!accessCode() &&
          activeRoute.queryParams['accessCode'] !== accessCode()
        );
      }),
      // guard against infinite loops: block if 10+ navigation occurred within the last 500ms
      filter(() => {
        // if we run +10 times withing latest 500ms, we are likely in the infinite loop, so we should stop
        const isInLoop = bucket.getLength() >= 10;
        if (isInLoop) {
          if (ngDevMode) {
            console.warn(
              'Potential infinite loop detected in retainCollectionAccessCodeIfNeeded. Navigation changes are occurring too frequently.'
            );
          }
          return false;
        }
        bucket.increment();
        return true;
      }),
      // listen until the current injector/component is destroyed
      takeUntilDestroyed()
    )
    .subscribe(() => {
      // just add accessCode query param without reloading the page
      router.navigate([], {
        queryParams: {
          // persist the retain_access param making clear that it was shared by collection link
          ...createRetainAccessParams(),
          // reapply access code
          accessCode: accessCode(),
        },
        // merge with existing query params
        queryParamsHandling: 'merge',
        // do not add a new history entry
        replaceUrl: true,
      });
    });
}

export function createRetainCollectionAccessCodeLinkConfig(): RetainAccessCodeLinkConfig | null {
  const route = inject(ActivatedRoute);
  const queryParams = collectRouteQueryParams(route.snapshot);
  const store = inject(Store);
  const accessCode = store.selectSignal(
    CustomerPortalCollectionAuthQueries.getAccessCode
  );
  const storage = injectRetainAccessCodeStorage();

  if (
    !hasRetainCollectionAccessFlag({
      storage,
      queryParams,
      accessCode: accessCode(),
    })
  ) {
    // if we don't have query param and no access code in storage, we can not guarantee that user was logged in via collection so we just skip
    return null;
  }

  const router = inject(Router);
  // Do not add `requireSync` as `router.events` stream is asynchronous.
  const currentRouteRequiresAccessCode = toSignal(
    getActivatedRouteThatRequiresAccessCodeRetention({
      route,
      router,
      startWithCurrentRoute: true,
    })
  );

  return {
    enabled: computed(() => {
      return !!currentRouteRequiresAccessCode() && !!accessCode();
    }),
    accessCode,
  };
}

export function provideRetainCollectionAccessCodeLinkConfig() {
  return cosProvide({
    provide: RETAIN_ACCESS_CODE_LINK_CONFIG,
    useFactory: createRetainCollectionAccessCodeLinkConfig,
  });
}
